/**
 * Grading Company Icon Component
 * 
 * Displays grading company logos (PSA, CGC, BGS, TAG, SGC, etc.)
 * Place logo images in /public/grading/ folder with lowercase names:
 * - psa.png
 * - cgc.png
 * - bgs.png
 * - tag.png
 * - sgc.png
 */

// Grading company to icon mapping
const GRADING_ICONS: Record<string, string> = {
  PSA: '/grading/psa.png',
  CGC: '/grading/cgc.png',
  BGS: '/grading/bgs.png',
  TAG: '/grading/tag.png',
  SGC: '/grading/sgc.png',
};

export type GradingCompany = keyof typeof GRADING_ICONS;

interface GradingIconProps {
  company: string;
  size?: number;
  className?: string;
  title?: string;
}

export function GradingIcon({ company, size = 24, className = '', title }: GradingIconProps) {
  const iconPath = GRADING_ICONS[company.toUpperCase()];
  
  if (!iconPath) {
    // Fallback to text if icon not found
    return (
      <span 
        className={`inline-flex items-center justify-center font-bold text-xs bg-white/10 rounded ${className}`}
        style={{ width: size, height: size }}
        title={title || company}
      >
        {company.substring(0, 3)}
      </span>
    );
  }
  
  return (
    <img 
      src={iconPath} 
      alt={`${company} logo`}
      title={title || `${company} Grading`}
      width={size}
      height={size}
      className={`inline-block object-contain ${className}`}
      loading="eager"
      decoding="sync"
      onError={(e) => {
        // Fallback to text on error
        const img = e.target as HTMLImageElement;
        img.style.display = 'none';
      }}
    />
  );
}

// Export list of supported companies
export const SUPPORTED_GRADING_COMPANIES = Object.keys(GRADING_ICONS);

